import { Accordion, CheckBox, Message } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './PrivacyPolicyField.module.css';
import { TPersonalDetailsDataDE } from '../../PersonalDetailsDE';

const PrivacyPolicyField = () => {
  const { t } = useLingui();

  const {
    register,
    formState: { errors },
  } = useFormContext<TPersonalDetailsDataDE>();

  return (
    <div>
      <p className={styles.privacyPolicySection}>
        Hiermit bestätige ich und ggf. weitere Geschäftsführende, iwocas{' '}
        <a
          href="https://www.iwoca.de/compliance/allgemeine-geschaeftsbedingungen"
          target="_blank"
          rel="noreferrer"
        >
          Allgemeinen Geschäftsbedingungen
        </a>
        , den{' '}
        <a
          href="https://www.iwoca.de/compliance/datenschutzhinweis"
          target="_blank"
          rel="noreferrer"
        >
          Datenschutzhinweis
        </a>{' '}
        und den{' '}
        <a
          href="https://www.iwoca.de/compliance/schufa-creditreform-hinweise"
          target="_blank"
          rel="noreferrer"
        >
          Hinweis zum Datentransfer an die Schufa Holding AG
        </a>{' '}
        gelesen zu haben und stimme diesen ausdrücklich zu. Außerdem entbinde
        ich bzw. wir die iwoca Deutschland GmbH, sowie iwoca Management S.à r.l.
        handelnd im Namen der iwoca SCSp SICAV-SIF vom Bankgeheimnis, um einen
        Datenaustausch und die Bearbeitung der Kreditanfrage zu ermöglichen.
      </p>
      <Accordion
        className={clsx(
          styles.privacyPolicyAccordionContainer,
          styles.privacyPolicyAccordion,
        )}
        titleClassName={styles.privacyPolicyAccordionTitle}
        title={t`Why do we need your consent?`}
        frame={false}
        small
      >
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`How do we handle your data?`}
          frame={false}
          small
        >
          <p>
            Datensicherheit steht bei iwoca ganz oben. Wir geben Ihre Daten nur
            an enge Partner*innen weiter, wenn dies zur Bearbeitung Ihres
            Kreditantrags notwendig ist. Wir werden Ihre Daten niemals an Dritte
            weitergeben, die nicht in direktem Zusammenhang mit Ihrem Antrag
            stehen. Weitere Informationen finden Sie{' '}
            <a
              href="https://www.iwoca.de/compliance/datenschutzhinweis"
              target="_blank"
              rel="noreferrer"
            >
              hier
            </a>
            .
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Why do we collect your Schufa information?`}
          frame={false}
          small
        >
          <p>
            Mithilfe der Auskunft können wir unsere Kreditanalyse beschleunigen.
            Ihre Bonität fließt zwar in unsere Kreditanalyse ein, entscheidend
            ist sie aber nur dann, wenn harte Negativmerkmale eingetragen sind.
            Im Klartext heißt das: Eine umstrittene Telefonrechnung von 2016 ist
            unproblematisch, wohingegen wir uns Probleme bei der Rückzahlung
            früherer Kredite genauer anschauen müssen. Keine Sorge: 90 % der
            deutschen Schufa-Dateien haben nur positive Einträge.
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Will my Schufa entry get worse as a result of the information?`}
          frame={false}
          small
        >
          <p>
            Nein, unsere Schufa-Abfrage hat keinen Einfluss auf Ihre Auskunft.
            Wir stellen lediglich eine “scoreneutrale” Konditionsanfrage, welche
            für andere Unternehmen nicht sichtbar ist.
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Why do we need the consent of all managing directors?`}
          frame={false}
          small
        >
          <p>
            In der Regel brauchen wir die Zustimmung aller weiteren
            Geschäftsführenden, da wir im Rahmen der Kreditanalyse auch ihre
            persönliche Bonität prüfen. Um den Prozess unkompliziert zu halten
            und Ihre Kreditanfrage schnellstmöglich bearbeiten zu können, bitten
            wir Sie, die Einwilligung Ihre Kolleg*innen einzuholen. Sollten
            weitere Geschäftsführende dem nicht zustimmen, können Sie sich gerne
            mit uns in Verbindung setzen.
          </p>
        </Accordion>
      </Accordion>
      <p className={styles.privacyPolicySection}>
        Falls Sie weitere Fragen zur Bearbeitung Ihrer Daten haben oder mehr zum
        Thema Schufa und Co. wissen möchten, schreiben Sie uns einfach eine
        E-Mail an{' '}
        <a href="mailto:support@iwoca.de" target="_blank" rel="noreferrer">
          support@iwoca.de
        </a>{' '}
        oder rufen Sie unter{' '}
        <a href="tel:06994323020" target="_blank" rel="noreferrer">
          069 9432 3020
        </a>{' '}
        an.
      </p>
      <CheckBox
        wrapperClassName={styles.privacyPolicyCheckboxContainer}
        className={clsx(styles.privacyPolicyCheckbox, {
          [styles.privacyPolicyCheckboxError]:
            errors.privacyPolicyAgreement?.message,
        })}
        labelProps={{ className: styles.privacyPolicyLabel }}
        {...register('privacyPolicyAgreement')}
        label={t`I’ve read and agreed to all terms & conditions`}
      />
      {errors.privacyPolicyAgreement?.message && (
        <Message
          className={styles.privacyPolicyError}
          message={errors.privacyPolicyAgreement?.message}
          type="error"
        />
      )}
    </div>
  );
};

export default PrivacyPolicyField;
